// Belajar interaktif: daftar modul per mapel dan pemutar modul langkah demi langkah.
// Jenis langkah:
//   materi : narasi TAKA + (opsional) widget interaktif dengan tugas yang harus diselesaikan
//   kuis   : kuis mini (pilihan ganda, isian angka, atau benar/salah) dengan petunjuk & pembahasan
// Setelah langkah terakhir selalu muncul rangkuman + tombol latihan soal topik modul ini.
import { $, $$, layarSentuh } from '../core/dom.js';
import { esc, F } from '../core/format.js';
import { richText, miring } from '../core/richtext.js';
import { store } from '../core/storage.js';
import { S, panggil } from '../app/state.js';
import { show } from '../app/screens.js';
import { mascot } from '../app/mascot.js';
import { sfx } from '../app/sound.js';
import { confetti } from '../app/confetti.js';
import { bukaPanelTaka, tutupTaka } from '../app/taka.js';
import { dukungTTS, bicara, berhentiBicara } from '../app/suara.js';
import { bukaRumus, kartuUntuk } from '../app/rumus.js';
import { mulaiKuis } from '../app/kuis.js';
import { MODE_DEMO } from '../app/riwayat.js';
import { keBeranda } from '../app/beranda.js';
import { MODUL, WIDGET, ELEMEN_URUT } from './daftar.js';
import { goyang, letup, animasi } from './alat.js';
import { bacaanHTML } from '../app/jawaban.js';
import { catatLangkah, catatModul, progresBelajar, simpanProgresBelajar, saatBerubah } from '../game/profil.js';
import { rayakan } from '../game/ui.js';
import { videoModul as daftarVideo } from './video.js';
import { bukaVideo, videoTersimpan } from '../app/pemutar.js';

const HURUF = 'ABCDEF';
const M = { modul: null, i: 0, beres: new Set(), kuis: {}, bersih: null, bacaOtomatis: false, mulai: 0, dilaporkan: false };

// Laporkan aktivitas belajar ke server untuk statistik admin (lib/belajar.js). Gagal = diam.
function lapor(aksi, data = {}) {
  if (MODE_DEMO || !M.modul || !navigator.onLine) return;
  fetch('/api/belajar', {
    method: 'POST', credentials: 'same-origin', keepalive: true,
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ aksi, modul: M.modul.id, ...data }),
  }).catch(() => {});
}

// ---------- progres (per nama, di profil yang disinkronkan ke server; lihat src/game/profil.js) ----------
export const progresModul = id => progresBelajar(id);
const simpanProgres = (id, data) => simpanProgresBelajar(id, data);
// Daftar modul yang sedang terbuka digambar ulang bila profil berubah (mis. setelah sinkron dari perangkat lain).
let mapelDaftar = null;
saatBerubah(() => { const scr = $('#scr-belajar'); if (scr && !scr.hidden && mapelDaftar) gambarDaftar(mapelDaftar); });
export const daftarModul = mapel => MODUL[mapel] || [];
export function ringkasProgres(mapel) {
  const ms = daftarModul(mapel);
  return { total: ms.length, selesai: ms.filter(m => progresModul(m.id)?.selesai).length };
}

// Teks polos untuk suara & konteks TAKA (tanpa sintaks teks kaya).
const polos = t => String(t || '').replace(/\[\[([^\]|]+)\|([^\]]+)\]\]/g, '$1/$2').replace(/\*\*/g, '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

// ---------- daftar modul ----------
export function keBelajar(mapel = 'matematika') {
  tutupTaka();
  berhentiBicara();
  mapelDaftar = mapel;
  gambarDaftar(mapel);
  show('belajar');
}

function gambarDaftar(mapel) {
  const ms = daftarModul(mapel);
  const nama = S.konten.config.mapel.find(m => m.id === mapel)?.nama || mapel;
  const rp = ringkasProgres(mapel);
  const lanjut = ms.filter(m => { const p = progresModul(m.id); return p && !p.selesai; }).sort((a, b) => progresModul(b.id).waktu - progresModul(a.id).waktu)[0];
  const kartu = m => {
    const p = progresModul(m.id);
    const persen = p?.selesai ? 100 : p ? Math.round(((p.terakhir || 0) / m.langkah.length) * 100) : 0;
    return `<button class="bl-modul warna-${esc(m.warna || 'sky')}${p?.selesai ? ' beres' : ''}" type="button" data-modul="${esc(m.id)}">
      <span class="bl-ikon" aria-hidden="true">${m.ikon || ''}</span>
      <span class="bl-isi"><b>${esc(m.judul)}</b><span>${esc(m.ringkas)}</span>
        <span class="bl-meta"><span>${m.langkah.length} langkah · ±${m.menit || 8} menit</span>${videoModul(m.id).length ? '<span class="bl-video">🎬 Ada video</span>' : ''}${p?.selesai ? `<span class="bl-bintang" aria-label="${p.bintang || 1} bintang">${'★'.repeat(p.bintang || 1)}${'☆'.repeat(3 - (p.bintang || 1))}</span>` : ''}</span>
        <span class="bl-prog" aria-hidden="true"><i style="width:${persen}%"></i></span></span>
    </button>`;
  };
  const grup = ELEMEN_URUT.map(el => {
    const isi = ms.filter(m => m.elemen === el);
    return isi.length ? `<section class="bl-grup"><h3>${esc(el)}</h3><div class="bl-grid">${isi.map(kartu).join('')}</div></section>` : '';
  }).join('');
  $('#scr-belajar').innerHTML = `
    <div class="bl-atas card">
      <button class="btn btn-sm" type="button" data-kembali>← Beranda</button>
      <div class="grow"><p class="eyebrow">Ayo belajar dulu sebelum ujian</p><h2>Belajar ${esc(nama)}</h2></div>
      <span class="pill bl-ringkas">${rp.selesai}/${rp.total} modul selesai</span>
    </div>
    ${lanjut ? `<button class="bl-lanjut card" type="button" data-modul="${esc(lanjut.id)}">${mascot('happy', 56)}<span><b>Lanjutkan: ${esc(lanjut.judul)}</b><span>Langkah ${(progresModul(lanjut.id).terakhir || 0) + 1} dari ${lanjut.langkah.length}</span></span><span class="btn btn-go">Lanjut →</span></button>` : ''}
    <div class="bl-daftar scroll">${grup || '<div class="card">Modul untuk mapel ini segera hadir.</div>'}</div>`;
  $('[data-kembali]', $('#scr-belajar')).onclick = () => { sfx.tap(); keBeranda(); };
  $$('[data-modul]', $('#scr-belajar')).forEach(b => b.onclick = () => { sfx.tap(); bukaModul(b.dataset.modul); });
}

// ---------- pemutar modul ----------
export function bukaModul(id) {
  const m = Object.values(MODUL).flat().find(x => x.id === id);
  if (!m) return keBelajar();
  const p = progresModul(id);
  M.modul = m;
  M.kuis = {};
  M.beres = new Set();
  // Lanjutkan dari langkah terakhir; langkah sebelumnya dianggap sudah dikerjakan.
  M.i = p && !p.selesai ? Math.min(p.terakhir || 0, m.langkah.length - 1) : 0;
  M.terjauh = M.i;
  for (let k = 0; k < M.i; k++) M.beres.add(k);
  M.bacaOtomatis = store.get('tka-belajar-baca', false) && dukungTTS;
  M.mulai = Date.now();
  M.dilaporkan = false;
  lapor('buka', { langkah: M.i });
  const layar = $('#scr-modul');
  layar.innerHTML = `
    <div class="ml-bar card">
      <button class="btn btn-sm" type="button" data-keluar aria-label="Kembali ke daftar modul">←</button>
      <div class="ml-judul grow"><span class="pill">${esc(m.elemen)}</span><b>${esc(m.judul)}</b></div>
      <div class="ml-titik" aria-hidden="true"></div>
      ${videoModul(m.id).length ? '<button class="btn btn-sm ml-video" type="button" data-video aria-label="Tonton video materi">🎬<span class="lbl"> Video</span></button>' : ''}
      ${dukungTTS ? '<button class="btn btn-sm ml-baca" type="button" data-baca aria-pressed="false"><svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor"/><path d="M16 8.5a5 5 0 0 1 0 7M18.5 6a8.5 8.5 0 0 1 0 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg><span class="lbl">Bacakan</span></button>' : ''}
      ${m.rumus?.length && kartuUntuk(m.rumus, m.mapel).length ? '<button class="btn btn-sm btn-sky" type="button" data-rumus><span class="lbl">Kartu </span>Rumus</button>' : ''}
    </div>
    <div class="ml-stage">
      <article class="ml-slide card" aria-live="polite"></article>
      <section class="taka card taka-laci" id="taka-belajar" hidden aria-label="Tanya TAKA tentang materi ini"></section>
    </div>
    <div class="ml-umpan-lantai" role="status" hidden></div>
    <div class="ml-nav">
      <button class="btn" type="button" data-mundur>← Sebelumnya</button>
      <button class="taka-launch ml-tanya" type="button" data-taka>${mascot('wow', 42)}<span>Bingung?<b>Tanya TAKA</b></span></button>
      <span class="ml-hitung"></span>
      <button class="btn btn-go" type="button" data-maju>Lanjut →</button>
    </div>`;
  layar.querySelector('[data-keluar]').onclick = () => { sfx.tap(); tutupModul(); keBelajar(m.mapel); };
  layar.querySelector('[data-mundur]').onclick = () => pindah(-1);
  layar.querySelector('[data-maju]').onclick = () => pindah(1);
  layar.querySelector('[data-taka]').onclick = bukaTakaMateri;
  layar.querySelector('[data-rumus]')?.addEventListener('click', () => bukaRumus(m.rumus));
  layar.querySelector('[data-video]')?.addEventListener('click', e => { sfx.tap(); putarVideoModul(e.currentTarget); });
  const baca = layar.querySelector('[data-baca]');
  if (baca) {
    const label = () => { baca.setAttribute('aria-pressed', String(M.bacaOtomatis)); };
    baca.onclick = () => {
      M.bacaOtomatis = !M.bacaOtomatis;
      store.set('tka-belajar-baca', M.bacaOtomatis);
      label();
      if (M.bacaOtomatis) bacakanLangkah(); else { berhentiBicara(); $('.ml-maskot', layar)?.classList.remove('bicara'); }
    };
    label();
  }
  show('modul');
  // Modul yang dimulai dari awal: putar videonya dulu (sekali; setelah itu lewat tombol 🎬).
  const video = videoModul(m.id);
  M.videoTampil = !!video.length && M.i === 0 && !videoDitonton(m.id);
  render(0);
  if (M.videoTampil) {
    if (navigator.onLine !== false) putarVideoModul(null, { otomatis: true });
    else videoTersimpan(video[0]).then(ada => { if (ada && M.modul === m) putarVideoModul(null, { otomatis: true }); else selesaiVideo(); });
  }
}

// ---------- video materi sebelum modul dimulai (src/belajar/video.js) ----------
const KUNCI_VIDEO = 'tka-video-modul';
// Versi demo satu-file tidak membawa file video.
function videoModul(id) { return MODE_DEMO ? [] : daftarVideo(id); }
const videoDitonton = id => !!store.get(KUNCI_VIDEO, {})[id];
function selesaiVideo() {
  M.videoTampil = false;
  if (M.bacaOtomatis) bacakanLangkah();
}
function putarVideoModul(asal, { otomatis = false } = {}) {
  const m = M.modul, daftar = videoModul(m.id);
  if (!daftar.length) return selesaiVideo();
  M.videoTampil = true;
  berhentiBicara();
  let diputar = false;
  M.tutupVideo = bukaVideo({
    daftar, label: `Video materi: ${m.judul}`, labelTutup: otomatis ? 'Lewati' : '',
    akhir: { label: M.i === 0 && !M.beres.size ? 'Mulai belajar →' : 'Lanjut belajar →' },
    saatMain: () => { diputar = true; },
    saatTutup: ({ selesai }) => {
      if (diputar && M.modul === m) {
        store.set(KUNCI_VIDEO, { ...store.get(KUNCI_VIDEO, {}), [m.id]: Date.now() });
        lapor('video', { selesai });
      }
      M.tutupVideo = null;
      if (M.modul === m) selesaiVideo();
    },
    asal: asal || $('[data-maju]', $('#scr-modul')),
  }).tutup;
}

function tutupModul() {
  M.bersih?.(); M.bersih = null;
  M.tutupVideo?.();
  tutupTaka();
  berhentiBicara();
}

const jumlahLangkah = () => M.modul.langkah.length + 1; // + rangkuman
const langkahIni = () => M.modul.langkah[M.i] || null;
const perluDikerjakan = L => L && ((L.jenis === 'kuis') || (L.widget && L.tugas && !L.bebas));

function pindah(arah) {
  const L = langkahIni();
  if (arah > 0 && perluDikerjakan(L) && !M.beres.has(M.i)) {
    umpan(L.jenis === 'kuis' ? 'Jawab kuis mininya dulu, ya!' : `Selesaikan tugasnya dulu, yuk! <button type="button" class="tautan" data-lewati>Lewati saja</button>`, 'think');
    goyang($('.ml-tugas', $('#scr-modul')) || $('.ml-panggung', $('#scr-modul')));
    $('[data-lewati]', $('#scr-modul'))?.addEventListener('click', () => { lapor('lewati', { langkah: M.i }); M.beres.add(M.i); pindah(1); });
    sfx.no?.();
    return;
  }
  const baru = M.i + arah;
  if (baru < 0 || baru >= jumlahLangkah()) return;
  sfx.tap();
  // Langkah yang baru pertama kali dilewati di sesi ini dihitung untuk misi belajar.
  if (arah > 0 && baru > (M.terjauh ?? M.i)) { M.terjauh = baru; catatLangkah(); }
  M.i = baru;
  if (M.i < M.modul.langkah.length) simpanProgres(M.modul.id, { terakhir: Math.max(M.i, progresModul(M.modul.id)?.terakhir || 0) });
  render(arah);
}

function render(arah) {
  M.bersih?.(); M.bersih = null;
  clearTimeout(timerLantai);
  const lantai = $('#scr-modul .ml-umpan-lantai');
  if (lantai) lantai.hidden = true;
  berhentiBicara();
  const layar = $('#scr-modul'), slide = $('.ml-slide', layar), m = M.modul;
  const n = m.langkah.length, akhir = M.i >= n;
  $('.ml-titik', layar).innerHTML = Array.from({ length: n + 1 }, (_, k) => `<i class="${k < M.i ? 'lewat' : k === M.i ? 'kini' : ''}${m.langkah[k]?.jenis === 'kuis' ? ' kuis' : ''}"></i>`).join('');
  $('.ml-hitung', layar).textContent = akhir ? 'Rangkuman' : `Langkah ${M.i + 1} dari ${n}`;
  $('[data-mundur]', layar).disabled = M.i === 0;
  const maju = $('[data-maju]', layar);
  maju.hidden = akhir;
  maju.textContent = M.i === n - 1 ? 'Selesai →' : 'Lanjut →';
  if (akhir) { renderRangkuman(slide); animasiMasuk(slide, arah); return; }

  const L = m.langkah[M.i];
  const kuis = L.jenis === 'kuis';
  slide.className = `ml-slide card ${kuis ? 'jenis-kuis' : 'jenis-materi'}${L.widget || kuis || L.visual || L.bacaan ? '' : ' tanpa-panggung'}`;
  slide.innerHTML = `
    <div class="ml-narasi scroll">
      <div class="ml-tokoh"><span class="ml-maskot">${mascot(kuis ? 'think' : L.mood || 'happy', 78)}</span>
        ${dukungTTS ? '<button class="ml-ulang" type="button" data-dengar aria-label="Dengarkan penjelasan"><svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor"/><path d="M16 8.5a5 5 0 0 1 0 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg></button>' : ''}</div>
      <div class="ml-gelembung">
        <p class="eyebrow">${kuis ? 'Kuis mini' : esc(L.label || 'Ayo pahami')}</p>
        <h3>${richText(L.judul)}</h3>
        ${L.narasi ? `<div class="ml-teks">${richText(L.narasi).replace(/\{nama\}/g, panggil())}</div>` : ''}
        ${L.tugas && !kuis ? `<p class="ml-tugas"><b>Tugasmu:</b> ${richText(L.tugas)}</p>` : ''}
        <div class="ml-umpan" aria-live="polite"></div>
      </div>
    </div>
    <div class="ml-panggung scroll"></div>`;
  const panggung = $('.ml-panggung', slide);
  if (kuis) renderKuis(panggung, L);
  else {
    if (L.bacaan) panggung.insertAdjacentHTML('beforeend', bacaanMini(L.bacaan));
    if (L.visual) panggung.insertAdjacentHTML('beforeend', `<div class="ml-visual">${typeof L.visual === 'function' ? L.visual() : L.visual}</div>`);
    if (L.widget) {
      const buat = WIDGET[L.widget.tipe];
      const wadah = document.createElement('div');
      wadah.className = 'ml-widget';
      panggung.append(wadah);
      if (buat) M.bersih = buat(wadah, L.widget, konteksWidget()) || null;
      else wadah.textContent = `Widget "${L.widget.tipe}" belum tersedia.`;
    }
  }
  if (M.beres.has(M.i) && perluDikerjakan(L) && !kuis) umpan('Tugas ini sudah kamu selesaikan. Boleh dicoba lagi, lalu tekan Lanjut.', 'happy');
  $('[data-dengar]', slide)?.addEventListener('click', bacakanLangkah);
  perbaruiMaju();
  animasiMasuk(slide, arah);
  if (M.bacaOtomatis && !M.videoTampil) bacakanLangkah();
}

function animasiMasuk(slide, arah) {
  slide.scrollTop = 0;
  $$('.scroll', slide).forEach(s => { s.scrollTop = 0; });
  if (arah) animasi(slide, [{ opacity: 0, transform: `translateX(${arah > 0 ? 40 : -40}px)` }, { opacity: 1, transform: 'none' }], { duration: 320 });
}

function perbaruiMaju() {
  const L = langkahIni();
  const maju = $('#scr-modul [data-maju]');
  if (!maju) return;
  const kunci = !!(perluDikerjakan(L) && !M.beres.has(M.i));
  maju.classList.toggle('terkunci', kunci);
  maju.classList.toggle('ajak', !!(!kunci && L && perluDikerjakan(L)));
}

let timerLantai = 0;
function umpan(html, mood) {
  const el = $('#scr-modul .ml-umpan');
  if (!el) return;
  el.innerHTML = html ? `<p>${html}</p>` : '';
  if (mood) gantiMood(mood);
  // HP: gelembung TAKA sering sudah tergulir ke atas saat anak mengerjakan widget di bawah,
  // jadi umpan balik ikut ditampilkan sebentar di atas tombol navigasi.
  const lantai = $('#scr-modul .ml-umpan-lantai');
  if (!lantai) return;
  clearTimeout(timerLantai);
  const r = el.getBoundingClientRect();
  if (!html || !matchMedia('(max-width: 699px)').matches || (r.top > 70 && r.bottom < innerHeight - 90)) { lantai.hidden = true; return; }
  lantai.innerHTML = `<p>${html}</p>`;
  lantai.hidden = false;
  lantai.onclick = () => { lantai.hidden = true; };
  timerLantai = setTimeout(() => { lantai.hidden = true; }, 7000);
}
function gantiMood(mood) {
  const w = $('#scr-modul .ml-maskot');
  if (!w) return;
  const bicaraSekarang = w.classList.contains('bicara');
  w.innerHTML = mascot(mood, 78);
  w.classList.toggle('bicara', bicaraSekarang);
  letup(w);
}

// Pesan dari widget berasal dari kode (bukan masukan pengguna), jadi boleh berisi HTML
// seperti pecahan dari alat.js; sintaks **tebal** dan [[a|b]] juga diproses.
const kaya = html => miring(String(html || '').replace(/\[\[([^\]|]+)\|([^\]]+)\]\]/g, (_, a, b) => F(a.trim(), b.trim())).replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')).replace(/\{nama\}/g, panggil());

// API yang diterima widget.
function konteksWidget() {
  const i = M.i;
  return {
    nama: panggil(),
    selesai(pesan = 'Hebat! Tugas selesai. Tekan **Lanjut** untuk meneruskan.') {
      if (M.i !== i) return;
      const baru = !M.beres.has(i);
      M.beres.add(i);
      umpan(kaya(pesan), 'cheer');
      if (baru) { sfx.ok(); confetti(24); }
      perbaruiMaju();
    },
    ucap(teks, mood) { if (M.i === i) umpan(teks ? kaya(teks) : '', mood); },
    sfx,
    sudahSelesai: () => M.beres.has(i),
  };
}

// Teks bacaan pendek di langkah materi/kuis (soal membaca). `sorot` = kutipan yang disorot.
const bacaanMini = b => `<div class="ml-bacaan">${bacaanHTML({ judul: b.judul || '', jenis: '', teks: b.teks, bentuk: b.bentuk }, { sorot: b.sorot || [] })}</div>`;

// ---------- kuis mini ----------
function renderKuis(host, L) {
  const st = M.kuis[M.i] || (M.kuis[M.i] = { salah: 0, beres: false, benarPertama: false });
  const tipe = L.isian ? 'isian' : L.bs ? 'bs' : 'pg';
  let isi = `${L.bacaan ? bacaanMini(L.bacaan) : ''}<div class="kj-soal">${richText(L.soal)}</div>${L.visual ? `<div class="ml-visual">${typeof L.visual === 'function' ? L.visual() : L.visual}</div>` : ''}`;
  if (tipe === 'pg') isi += `<div class="opts kj-opsi">${L.pilihan.map((p, j) => `<button class="opt" type="button" data-j="${j}"><span class="opt-l">${HURUF[j]}</span><span class="opt-t">${richText(p)}</span><span class="tag"></span></button>`).join('')}</div>`;
  if (tipe === 'isian') isi += `<form class="kj-isian" data-isian><label><span class="sr-only">Jawabanmu</span><input class="input" inputmode="decimal" autocomplete="off" placeholder="Jawaban" aria-label="Jawabanmu"></label>${L.isian.satuan ? `<span class="kj-satuan">${esc(L.isian.satuan)}</span>` : ''}<button class="btn btn-sun" type="submit">Periksa</button></form>`;
  if (tipe === 'bs') isi += `<div class="bs kj-bs">${L.bs.map((x, j) => `<div class="bs-row" data-r="${j}"><div class="bs-t"><b>${HURUF[j]}.</b> ${richText(x.teks)}</div><div class="bs-btns"><button class="bs-b" type="button" data-r="${j}" data-v="1">Benar</button><button class="bs-b" type="button" data-r="${j}" data-v="0">Salah</button></div></div>`).join('')}</div><button class="btn btn-sun kj-periksa" type="button" data-periksa disabled>Periksa</button>`;
  isi += '<div class="kj-bahas" hidden></div>';
  host.innerHTML = `<div class="kuis-mini">${isi}</div>`;

  const bahas = $('.kj-bahas', host);
  const tampilBahas = (benar, kunciHTML = '') => {
    bahas.hidden = false;
    bahas.className = `kj-bahas ${benar ? 'benar' : 'salah'}`;
    bahas.innerHTML = `${kunciHTML ? `<p><b>Jawaban yang benar:</b> ${kunciHTML}</p>` : ''}${L.bahas ? `<p>${richText(L.bahas)}</p>` : ''}`;
    animasi(bahas, [{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }], { duration: 300 });
  };
  const nilai = (benar, el, kunciHTML) => {
    if (st.beres) return;
    if (benar) {
      st.beres = true;
      if (!st.salah) st.benarPertama = true;
      lapor('kuis', { langkah: M.i, benar: !st.salah });
      M.beres.add(M.i);
      sfx.ok(); confetti(30);
      umpan(st.salah ? `Nah, betul! Kamu pantang menyerah, ${panggil()}.` : `Tepat sekali, ${panggil()}! 🎉`, 'cheer');
      tampilBahas(true);
    } else {
      st.salah++;
      sfx.no(); goyang(el);
      if (st.salah >= 2) {
        st.beres = true;
        lapor('kuis', { langkah: M.i, benar: false });
        M.beres.add(M.i);
        umpan('Tidak apa-apa, kita lihat pembahasannya bersama, ya.', 'sad');
        tampilBahas(false, kunciHTML);
      } else if (st.alasanSalah) umpan(`Belum tepat. ${richText(st.alasanSalah)}${L.petunjuk ? ` <br><b>Petunjuk:</b> ${richText(L.petunjuk)}` : ''}`, 'think');
      else umpan(L.petunjuk ? `Hampir! Petunjuk: ${richText(L.petunjuk)}` : 'Hampir! Coba periksa lagi pelan-pelan.', 'think');
    }
    perbaruiMaju();
  };

  if (tipe === 'pg') {
    $$('.opt', host).forEach(b => b.addEventListener('click', () => {
      if (st.beres || b.disabled) return;
      const j = +b.dataset.j, benar = j === L.kunci;
      st.alasanSalah = !benar && L.alasan?.[j] ? L.alasan[j] : '';
      b.classList.add(benar ? 'ok' : 'no');
      b.querySelector('.tag').textContent = benar ? 'Benar' : 'Belum tepat';
      if (!benar) b.disabled = true;
      nilai(benar, b, `${HURUF[L.kunci]}. ${richText(L.pilihan[L.kunci])}`);
      if (st.beres) $$('.opt', host).forEach((x, k) => { x.disabled = true; if (k === L.kunci) x.classList.add('ok'); });
    }));
  }
  if (tipe === 'isian') {
    const f = $('[data-isian]', host), inp = $('input', f);
    f.addEventListener('submit', e => {
      e.preventDefault();
      if (st.beres) return;
      const v = Number(inp.value.replace(/\s/g, '').replace(/\./g, '').replace(',', '.'));
      if (inp.value.trim() === '' || Number.isNaN(v)) { umpan('Tulis jawabanmu dengan angka, ya.', 'think'); return; }
      const benar = Math.abs(v - L.isian.jawaban) < 1e-6;
      nilai(benar, f, `${String(L.isian.jawaban).replace('.', ',')}${L.isian.satuan ? ' ' + esc(L.isian.satuan) : ''}`);
      if (st.beres) { inp.disabled = true; f.querySelector('button').disabled = true; inp.value = String(L.isian.jawaban).replace('.', ','); }
      else inp.select();
    });
    if (!layarSentuh()) setTimeout(() => inp.focus({ preventScroll: true }), 60);
  }
  if (tipe === 'bs') {
    const pilihan = {};
    const periksa = $('[data-periksa]', host);
    $$('.bs-b', host).forEach(b => b.addEventListener('click', () => {
      if (st.beres) return;
      const r = +b.dataset.r;
      pilihan[r] = b.dataset.v === '1';
      $$(`.bs-b[data-r="${r}"]`, host).forEach(x => x.classList.toggle('on', x === b));
      periksa.disabled = Object.keys(pilihan).length < L.bs.length;
    }));
    periksa.addEventListener('click', () => {
      const salahBaris = L.bs.map((x, j) => pilihan[j] !== x.benar);
      const benar = !salahBaris.some(Boolean);
      $$('.bs-row', host).forEach((row, j) => row.classList.toggle('salah', salahBaris[j]));
      nilai(benar, periksa, L.bs.map((x, j) => `${HURUF[j]} ${x.benar ? 'Benar' : 'Salah'}`).join(', '));
      if (st.beres) { periksa.disabled = true; $$('.bs-b', host).forEach(b => { b.disabled = true; }); $$('.bs-row', host).forEach((row, j) => { row.classList.remove('salah'); row.classList.add(L.bs[j].benar === pilihan[j] ? 'benar' : 'salah'); }); }
    });
  }
  if (st.beres) { umpan('Kuis ini sudah kamu jawab. Tekan Lanjut untuk meneruskan.', 'happy'); tampilBahas(true); $$('button, input', host).forEach(x => { x.disabled = true; }); }
}

// ---------- rangkuman ----------
function renderRangkuman(slide) {
  const m = M.modul;
  const kuis = m.langkah.map((L, k) => [L, k]).filter(([L]) => L.jenis === 'kuis');
  const benar = kuis.filter(([, k]) => M.kuis[k]?.benarPertama).length;
  const sebelumnya = progresModul(m.id);
  // Kuis yang dilewati karena melanjutkan sesi lama tidak dihitung salah.
  const dikerjakan = kuis.filter(([, k]) => M.kuis[k]).length;
  const rasio = dikerjakan ? benar / dikerjakan : 1;
  const bintangSesi = rasio >= 0.99 ? 3 : rasio >= 0.5 ? 2 : 1;
  const bintang = Math.max(sebelumnya?.bintang || 0, bintangSesi);
  simpanProgres(m.id, { selesai: true, terakhir: m.langkah.length, bintang });
  if (!M.dilaporkan) {
    M.dilaporkan = true;
    catatModul(m.id, bintangSesi);
    rayakan({ tunda: 1200 });
    lapor('selesai', { bintang: bintangSesi, benar, total: dikerjakan, detik: Math.min(6 * 3600, Math.round((Date.now() - M.mulai) / 1000)) });
  }
  const ms = daftarModul(m.mapel), idx = ms.indexOf(m), berikut = ms[idx + 1];
  slide.className = 'ml-slide card jenis-rangkuman';
  slide.innerHTML = `
    <div class="ml-narasi scroll">
      <div class="ml-tokoh"><span class="ml-maskot">${mascot('cheer', 96, 'bounce')}</span></div>
      <div class="ml-gelembung">
        <p class="eyebrow">Modul selesai</p>
        <h3>Keren, ${panggil()}! Kamu sudah belajar ${esc(m.judul)}.</h3>
        <p class="rk-bintang" aria-label="${bintang} dari 3 bintang">${'<span>★</span>'.repeat(bintang)}${'<span class="kosong">★</span>'.repeat(3 - bintang)}</p>
        ${dikerjakan ? `<p class="muted">Kuis mini benar di percobaan pertama: <b>${benar} dari ${dikerjakan}</b>.</p>` : ''}
      </div>
    </div>
    <div class="ml-panggung scroll">
      <div class="rk-poin"><h4>Yang perlu diingat</h4><ul>${(m.ringkasan || []).map(r => `<li>${richText(r)}</li>`).join('')}</ul></div>
      <div class="rk-aksi">
        ${m.topik?.length ? `<button class="btn btn-go" type="button" data-latihan>Latihan 10 soal topik ini</button>` : ''}
        ${berikut ? `<button class="btn btn-sky" type="button" data-berikut>Modul berikutnya: ${esc(berikut.judul)}</button>` : ''}
        <button class="btn" type="button" data-daftar>Kembali ke daftar modul</button>
        <button class="btn btn-sm" type="button" data-ulang>Ulangi modul ini</button>
      </div>
    </div>`;
  sfx.win?.(); confetti(80);
  $('[data-latihan]', slide)?.addEventListener('click', () => { lapor('latihan'); tutupModul(); S.mapel = m.mapel; mulaiKuis({ jumlah: 10, topik: m.topik, modul: m.id }); });
  $('[data-berikut]', slide)?.addEventListener('click', () => { tutupModul(); bukaModul(berikut.id); });
  $('[data-daftar]', slide).addEventListener('click', () => { tutupModul(); keBelajar(m.mapel); });
  $('[data-ulang]', slide).addEventListener('click', () => { simpanProgres(m.id, { terakhir: 0, selesai: true }); M.i = 0; M.terjauh = 0; M.beres.clear(); M.kuis = {}; M.mulai = Date.now(); M.dilaporkan = false; lapor('buka', { langkah: 0 }); render(-1); });
  if (M.bacaOtomatis) bicaraMaskot(`Keren, ${polos(panggil())}! Kamu sudah menyelesaikan ${m.judul}. ${(m.ringkasan || []).map(polos).join('. ')}`);
}

// ---------- suara ----------
function bicaraMaskot(teks) {
  const w = $('#scr-modul .ml-maskot');
  bicara(teks, { onMulai: () => w?.classList.add('bicara'), onSelesai: () => w?.classList.remove('bicara') });
}
function bacakanLangkah() {
  const L = langkahIni();
  if (!L) return;
  const pilihan = L.jenis === 'kuis' && L.pilihan ? ' ' + L.pilihan.map((p, j) => `${HURUF[j]}. ${polos(p)}`).join('. ') : '';
  bicaraMaskot([polos(L.judul), polos(L.narasi).replace(/\{nama\}/g, S.nama.split(/\s+/)[0] || ''), L.tugas ? 'Tugasmu: ' + polos(L.tugas) : '', L.jenis === 'kuis' ? polos(L.soal) + pilihan : ''].filter(Boolean).join('. '));
}

// ---------- TAKA untuk materi ----------
function konteksMateri() {
  const m = M.modul, L = langkahIni();
  return {
    mapel: S.konten.config.mapel.find(x => x.id === m.mapel)?.nama || m.mapel,
    id: m.id,
    elemen: m.elemen,
    modul: m.judul,
    ringkasan: (m.ringkasan || []).map(polos),
    langkah: L ? { judul: polos(L.judul), teks: [polos(L.narasi), L.tugas ? 'Tugas: ' + polos(L.tugas) : '', L.bacaan ? 'Teks bacaan: ' + polos(L.bacaan.teks) : '', polos(L.konteks)].filter(Boolean).join(' ') } : { judul: 'Rangkuman', teks: '' },
    kuis: L?.jenis === 'kuis' ? {
      soal: polos(L.soal),
      pilihan: (L.pilihan || L.bs?.map(x => x.teks) || []).map(polos),
      kunci: L.pilihan ? `${HURUF[L.kunci]}. ${polos(L.pilihan[L.kunci])}` : L.isian ? `${L.isian.jawaban} ${L.isian.satuan || ''}`.trim() : L.bs.map((x, j) => `${HURUF[j]} ${x.benar ? 'benar' : 'salah'}`).join(', '),
    } : null,
  };
}

function bukaTakaMateri() {
  sfx.tap();
  berhentiBicara();
  const m = M.modul;
  const panel = $('#taka-belajar');
  bukaPanelTaka(panel, {
    kunci: m, mode: 'materi', konteks: konteksMateri, maks: 20,
    sapa: n => `Hai ${n}! Aku TAKA. Kita sedang belajar ${esc(m.judul)}. Kalau ada yang membingungkan, tanya aku, ya!`,
    saran: () => (langkahIni()?.jenis === 'kuis' ? ['Beri aku petunjuk', 'Jelaskan konsepnya lagi'] : ['Jelaskan bagian ini lagi', 'Kasih contoh lain', 'Kenapa caranya begitu?']),
    placeholder: 'Tanya tentang materi ini...',
    sisa: n => `Sisa ${n} pertanyaan untuk materi ini.`,
    habis: 'Pertanyaan untuk materi ini sudah habis. Coba ulangi langkahnya pelan-pelan, ya!',
    onTutup: () => $('#scr-modul [data-taka]')?.focus({ preventScroll: true }),
  });
}

// Tombol panah kiri/kanan untuk berpindah langkah (kecuali sedang mengetik).
document.addEventListener('keydown', e => {
  if ($('#scr-modul')?.hidden !== false || e.target.closest('input, textarea, select, .taka') || e.altKey || e.ctrlKey || e.metaKey) return;
  if (e.key === 'ArrowRight') pindah(1);
  if (e.key === 'ArrowLeft') pindah(-1);
});
